import { Dispatch, SetStateAction, useEffect, useMemo } from "react";
import { useQueryState } from "nuqs";
import throttle from "lodash/throttle";

import { useAccountDetailsContext } from "@/modules/pipeline/account-details/context";
import { WIDGETS_SEARCH_QUERY_PARAM_NAMES } from "@/components/shared/widgets/constants";
import { useEmailTemplatesSidebarContext } from "@/modules/email-templates-sidebar/context";
import { useCheckCampaignHasEmailAutomation } from "@/modules/email-templates-sidebar/queries";
import { ACCOUNT_SEND_EMAIL_MODAL_ID } from "@/components/modals/account-send-email-modal";
import { modalHelpers } from "shared/lib/helpers/modalHelpers";
import { asyncWaitFor } from "@/helpers/context";
import { ContactDetailI } from "shared/lib/interfaces/account";

export const useEmailOnStart = () => {
  const [email, setEmail] = useQueryState(
    WIDGETS_SEARCH_QUERY_PARAM_NAMES.EMAIL
  );

  const { contacts, campaign, setFocusedContact } = useAccountDetailsContext();

  const { onOpen: onOpenEmailTemplatesSidebar } =
    useEmailTemplatesSidebarContext();

  const {
    data: checkIsCampaignEmailSetupData,
    isSuccess: isEmailAutomationSetupLoaded,
  } = useCheckCampaignHasEmailAutomation(campaign?.id);

  const isEmailAutomationSetup = useMemo(
    () => checkIsCampaignEmailSetupData?.data.is_email_setup,
    [checkIsCampaignEmailSetupData]
  );

  /**
   * Throttle the start call to avoid spamming the server
   * and to avoid calling the server for each key stroke
   */
  const throttledOpenEmailTemplatesSidebar = throttle(async () => {
    const emailToOpen = email;
    setEmail(null);

    if (!emailToOpen) return;

    const contact = contacts?.find((contact) => contact.email === emailToOpen);

    if (!contact) return;

    setFocusedContact(contact);

    await asyncWaitFor(
      setFocusedContact as Dispatch<SetStateAction<ContactDetailI>>,
      {
        expectedValue: (focusedContact: ContactDetailI) => {
          return focusedContact.email === emailToOpen;
        },
      }
    );

    if (isEmailAutomationSetup) {
      /**
       * TODO: Figure out why timeout is still needed
       * sidebar attemps to open and closes midway
       */
      setTimeout(() => {
        onOpenEmailTemplatesSidebar();
      }, 500);
    } else {
      modalHelpers.trigger(ACCOUNT_SEND_EMAIL_MODAL_ID);
    }
  }, 1000);

  useEffect(() => {
    if (isEmailAutomationSetupLoaded) throttledOpenEmailTemplatesSidebar();
  }, [contacts, email, isEmailAutomationSetupLoaded]);
};
